import React, { useState } from "react";
import {
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Calendar,
  Database,
  Settings,
  TrendingUp,
  Filter,
  ChevronDown,
  ChevronRight,
  Zap,
  RotateCcw,
  Trash2,
  ArrowUpDown,
  FileDigit,
  DecimalsArrowLeft,
} from "lucide-react";
const ExecutionSummaryModal = ({ summaryData, isOpen, onClose }) => {
  // Close modal on Escape key press
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      const modal = document.getElementById("execution-summary-modal");
      if (modal && !modal.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const data = summaryData;

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleString();
  };

  const getStatusIcon = (status) => {
    return status === "success" ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <AlertCircle className="w-4 h-4 text-red-500" />
    );
  };

  const toggleAccordion = (section) => {
    setActiveAccordion(activeAccordion === section ? null : section);
  };

  const AccordionSection = ({
    id,
    title,
    icon: Icon,
    children,
    defaultOpen = false,
  }) => {
    const isOpen = activeAccordion === id;

    return (
      <div className="border rounded-lg overflow-hidden mb-4">
        <button
          onClick={() => toggleAccordion(id)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-800">{title}</span>
          </div>
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-600" />
          )}
        </button>
        {isOpen && <div className="p-4 bg-white border-t">{children}</div>}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      id="execution-summary-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full h-[95vh] overflow-hidden ">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Execution Summary</h2>
              <p className="text-blue-100 text-sm">File: {data.fileName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-lg p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(95vh-140px)]">
          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700">
                <Clock className="w-5 h-5" />
                <span className="font-semibold text-sm">Execution Time</span>
              </div>
              <p className="text-2xl font-bold text-green-800">
                {data.executionTimeInMs}ms
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-700">
                <Database className="w-5 h-5" />
                <span className="font-semibold text-sm">Rows Processed</span>
              </div>
              <p className="text-2xl font-bold text-blue-800">
                {data.totalRowsProcessed ||
                  data.inputColumnValidation?.totalRows ||
                  0}
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-purple-700">
                <Settings className="w-5 h-5" />
                <span className="font-semibold text-sm">Rules Executed</span>
              </div>
              <p className="text-2xl font-bold text-purple-800">
                {data.rulesExecution?.totalRules || 0}
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-orange-700">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold text-sm">Success Rate</span>
              </div>
              <p className="text-2xl font-bold text-orange-800">
                {data.rulesExecution?.totalRules > 0
                  ? Math.round(
                      ((data.rulesExecution.successfulRules ||
                        data.rulesExecution.executedRules?.filter(
                          (r) => r.status === "success"
                        ).length ||
                        0) /
                        data.rulesExecution.totalRules) *
                        100
                    )
                  : 0}
                %
              </p>
            </div>
          </div>

          {/* Additional Stats Row */}
          {(data.InputColumnProcessing || data.sorting || data.rowRemoval) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {/* Input Column date conversions */}
              {data.InputColumnProcessing?.dateConversions?.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-indigo-700">
                    <Calendar className="w-5 h-5" />
                    <span className="font-semibold text-sm">
                      Date Conversions
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-indigo-800">
                    {data.InputColumnProcessing.dateConversions.length}
                  </p>
                </div>
              )}
              {/* Input Column integer conversions */}
              {data.InputColumnProcessing?.integerConversions?.length > 0 && (
                <div className="bg-indigo-50 border bg-gradient-to-tr from-indigo-200 to-indigo-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-indigo-700">
                    <FileDigit className="w-5 h-5" />
                    <span className="font-semibold text-sm">
                      Integer Conversions
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-indigo-800">
                    {data.InputColumnProcessing.integerConversions.length}
                  </p>
                </div>
              )}
              {/* Input Column absolute conversions */}
              {data.InputColumnProcessing?.absoluteConversions?.length > 0 && (
                <div className="bg-indigo-50 border bg-gradient-to-tr from-blue-200 to-blue-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-indigo-700">
                    <DecimalsArrowLeft className="w-5 h-5" />
                    <span className="font-semibold text-sm">
                      Absolute Conversions
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-indigo-800">
                    {data.InputColumnProcessing.absoluteConversions.length}
                  </p>
                </div>
              )}
              {data.InputColumnProcessing?.negateConversions?.length > 0 && (
                <div className="bg-indigo-50 border bg-gradient-to-tr from-red-200 to-red-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-indigo-700">
                    <RotateCcw className="w-5 h-5" />
                    <span className="font-semibold text-sm">
                      Negative Conversions
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-indigo-800">
                    {data.InputColumnProcessing.negateConversions.length}
                  </p>
                </div>
              )}

              {data.sorting?.totalOperations > 0 && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-cyan-700">
                    <ArrowUpDown className="w-5 h-5" />
                    <span className="font-semibold text-sm">
                      Sort Operations
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-cyan-800">
                    {data.sorting.totalOperations}
                  </p>
                </div>
              )}

              {data.rowRemoval?.totalRowsRemoved > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <Trash2 className="w-5 h-5" />
                    <span className="font-semibold text-sm">Rows Removed</span>
                  </div>
                  <p className="text-2xl font-bold text-red-800">
                    {data.rowRemoval.totalRowsRemoved}
                  </p>
                </div>
              )}

              {data.InputColumnProcessing?.totalChanges > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-700">
                    <Zap className="w-5 h-5" />
                    <span className="font-semibold text-sm">Total Changes</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-800">
                    {data.InputColumnProcessing.totalChanges}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Input Column Validation */}
          {data.inputColumnValidation?.validColumns?.length > 0 && (
            <AccordionSection
              id="input-validation"
              title="Input Column Validation"
              icon={Filter}
            >
              <div className="bg-white border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Column
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Has Data
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Sample Data
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Applied Rules
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.inputColumnValidation.validColumns.map(
                      (col, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm font-semibold">
                            {col.columnLetter}
                          </td>
                          <td className="px-4 py-3">
                            {col.hasData ? (
                              <span className="text-green-600 font-medium">
                                ✓ Yes
                              </span>
                            ) : (
                              <span className="text-red-600 font-medium">
                                ✗ No
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                            <div className="truncate">
                              {col.sampleData?.slice(0, 2).join(", ")}
                              {col.sampleData?.length > 2 && "..."}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {col.appliedRules?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {col.appliedRules.map((rule, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                                  >
                                    {rule}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">None</span>
                            )}
                          </td>
                        </tr>
                      )
                    )}
                    {/* Empty Column */}
                    {data.inputColumnValidation.emptyColumns &&
                      data.inputColumnValidation.emptyColumns.map(
                        (col, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono text-sm font-semibold">
                              {col.columnLetter}
                            </td>
                            <td className="px-4 py-3">
                              {col.hasData ? (
                                <span className="text-green-600 font-medium">
                                  ✓ Yes
                                </span>
                              ) : (
                                <span className="text-red-600 font-medium">
                                  ✗ No
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                              <div className="truncate">
                                {col.sampleData?.slice(0, 2).join(", ")}
                                {col.sampleData?.length > 2 && "..."}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {col.appliedRules?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {col.appliedRules.map((rule, idx) => (
                                    <span
                                      key={idx}
                                      className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                                    >
                                      {rule}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400">None</span>
                              )}
                            </td>
                          </tr>
                        )
                      )}
                  </tbody>
                </table>
              </div>
              {/* warning messages */}
              <div>
                {data.inputColumnValidation.warnings?.length > 0 && (
                  <ul className="p-2 list-disc pl-5">
                    {data.inputColumnValidation.warnings.map(
                      (warning, index) => (
                        <li key={index} className="text-yellow-600">
                          {warning}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </div>

              {/* Summary stats for validation */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-800">
                    {data.inputColumnValidation.validColumns?.length || 0}
                  </div>
                  <div className="text-sm text-green-600">Valid Columns</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-800">
                    {data.inputColumnValidation.invalidColumns?.length || 0}
                  </div>
                  <div className="text-sm text-red-600">Invalid Columns</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">
                    {data.inputColumnValidation.emptyColumns?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Empty Columns</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-800">
                    {data.inputColumnValidation.warnings?.length || 0}
                  </div>
                  <div className="text-sm text-yellow-600">Warnings</div>
                </div>
              </div>
            </AccordionSection>
          )}

          {/* Data Processing Details */}
          {data.InputColumnProcessing && (
            <AccordionSection
              id="data-processing"
              title="Data Preprocessing Details"
              icon={Zap}
            >
              {/* Date Conversions */}
              {data.InputColumnProcessing.dateConversions?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Date Conversions
                  </h4>
                  <div className="space-y-4">
                    {data.InputColumnProcessing.dateConversions.map(
                      (conv, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <span className="font-mono font-semibold text-lg">
                                Column {conv.column}
                              </span>
                              <div className="text-sm text-gray-600 mt-1">
                                {conv.inputFormat} → {conv.outputFormat}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-green-600">
                                {conv.successful}/{conv.totalProcessed}
                              </div>
                              <div className="text-sm text-gray-500">
                                successful
                              </div>
                            </div>
                          </div>

                          {conv.failed > 0 && (
                            <div className="bg-red-100 border border-red-200 rounded p-2 mb-3">
                              <span className="text-red-800 text-sm font-medium">
                                {conv.failed} conversions failed
                              </span>
                            </div>
                          )}

                          {/* Sample conversions */}
                          {conv.details && conv.details.length > 0 && (
                            <div className="bg-white rounded border">
                              <div className="px-3 py-2 bg-gray-100 border-b font-medium text-sm">
                                Sample Conversions
                              </div>
                              <div className="max-h-40 overflow-y-auto">
                                {conv.details.map((detail, idx) => (
                                  <div
                                    key={idx}
                                    className="px-3 py-2 border-b last:border-b-0 text-sm"
                                  >
                                    <div className="flex justify-between items-center">
                                      <span className="font-mono text-xs text-gray-500">
                                        {detail.cellAddress}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs ${
                                          detail.error
                                            ? "bg-red-100 text-red-800"
                                            : "bg-green-100 text-green-800"
                                        }`}
                                      >
                                        {detail.error ? "Failed" : "Success"}
                                      </span>
                                    </div>
                                    <div className="mt-1">
                                      <div className="text-gray-600 truncate">
                                        <span className="font-medium">
                                          Original:
                                        </span>{" "}
                                        {detail.originalValue}
                                      </div>
                                      {detail.convertedValue && (
                                        <div className="text-gray-800 truncate">
                                          <span className="font-medium">
                                            Converted:
                                          </span>{" "}
                                          {detail.convertedValue}
                                        </div>
                                      )}
                                      {detail.error && (
                                        <div className="text-red-600 text-xs">
                                          Error: {detail.error}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {/* {conv.details.length > 10 && (
                                <div className="px-3 py-2 text-center text-sm text-gray-500">
                                  ... and {conv.details.length - 10} more conversions
                                </div>
                              )} */}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Absolute Value Conversions */}
              {data.InputColumnProcessing.absoluteConversions?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Absolute Value Conversions
                  </h4>
                  <div className="space-y-4">
                    {data.InputColumnProcessing.absoluteConversions.map(
                      (conv, index) => (
                        <div
                          key={index}
                          className="bg-purple-50 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-mono font-semibold text-lg">
                              Column {conv.column}
                            </span>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-purple-600">
                                {conv.totalProcessed}
                              </div>
                              <div className="text-sm text-gray-500">
                                processed
                              </div>
                            </div>
                          </div>

                          {/* Sample conversions */}
                          {conv.details && conv.details.length > 0 && (
                            <div className="bg-white rounded border">
                              <div className="px-3 py-2 bg-gray-100 border-b font-medium text-sm">
                                Sample Conversions
                              </div>
                              <div className="max-h-40 overflow-y-auto">
                                {conv.details.map((detail, idx) => (
                                  <div
                                    key={idx}
                                    className="px-3 py-2 border-b last:border-b-0 text-sm"
                                  >
                                    <div className="flex justify-between items-center">
                                      <span className="font-mono text-xs text-gray-500">
                                        Row {detail.rowIndex}
                                      </span>
                                      <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                                        Absolute
                                      </span>
                                    </div>
                                    <div className="mt-1 flex justify-between">
                                      <div className="text-gray-600">
                                        <span className="font-medium">
                                          From:
                                        </span>{" "}
                                        {detail.originalValue}
                                      </div>
                                      <div className="text-purple-800 font-mono">
                                        <span className="font-medium">To:</span>{" "}
                                        {detail.convertedValue}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {/* {conv.details.length > 10 && (
                                <div className="px-3 py-2 text-center text-sm text-gray-500">
                                  ... and {conv.details.length - 10} more conversions
                                </div>
                              )} */}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Integer Conversions */}
              {data.InputColumnProcessing.integerConversions?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Integer Conversions
                  </h4>
                  <div className="space-y-4">
                    {data.InputColumnProcessing.integerConversions.map(
                      (conv, index) => (
                        <div
                          key={index}
                          className="bg-indigo-50 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-mono font-semibold text-lg">
                              Column {conv.column}
                            </span>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-indigo-600">
                                {conv.totalProcessed}
                              </div>
                              <div className="text-sm text-gray-500">
                                processed
                              </div>
                            </div>
                          </div>

                          {/* Sample conversions */}
                          {conv.details && conv.details.length > 0 && (
                            <div className="bg-white rounded border">
                              <div className="px-3 py-2 bg-gray-100 border-b font-medium text-sm">
                                Sample Conversions
                              </div>
                              <div className="max-h-40 overflow-y-auto">
                                {conv.details.map((detail, idx) => (
                                  <div
                                    key={idx}
                                    className="px-3 py-2 border-b last:border-b-0 text-sm"
                                  >
                                    <div className="flex justify-between items-center">
                                      <span className="font-mono text-xs text-gray-500">
                                        Row {detail.rowIndex}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs ${
                                          detail.convertedValue !== null
                                            ? "bg-indigo-100 text-indigo-800"
                                            : "bg-gray-100 text-gray-600"
                                        }`}
                                      >
                                        {detail.convertedValue !== null
                                          ? "Converted"
                                          : "Skipped"}
                                      </span>
                                    </div>
                                    <div className="mt-1">
                                      <div className="text-gray-600 truncate">
                                        <span className="font-medium">
                                          Original:
                                        </span>{" "}
                                        {detail.originalValue}
                                      </div>
                                      {detail.convertedValue !== null && (
                                        <div className="text-indigo-800 font-mono">
                                          <span className="font-medium">
                                            Converted:
                                          </span>{" "}
                                          {detail.convertedValue}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {/* {conv.details.length > 10 && (
                                <div className="px-3 py-2 text-center text-sm text-gray-500">
                                  ... and {conv.details.length - 10} more conversions
                                </div>
                              )} */}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Text Replacements */}
              {data.InputColumnProcessing.replacements?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Text Replacements
                  </h4>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-800">
                      {data.InputColumnProcessing.replacements.length}
                    </div>
                    <div className="text-sm text-blue-600">
                      Replacement Operations
                    </div>
                  </div>
                </div>
              )}

              {/* Summary */}
              {data.InputColumnProcessing.totalChanges > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800">
                      {data.InputColumnProcessing.totalChanges}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">
                      Total Changes Made
                    </div>
                  </div>
                </div>
              )}
            </AccordionSection>
          )}

          {/* Rules Execution */}
          {data.rulesExecution?.executedRules?.length > 0 && (
            <AccordionSection
              id="rules-execution"
              title="Rules Execution"
              icon={Settings}
            >
              <div className="space-y-3">
                {data.rulesExecution.executedRules.map((rule, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(rule.status)}
                        <div>
                          <span className="font-medium">{rule.ruleName}</span>
                          <span className="text-gray-500 text-sm ml-2">
                            ID: {rule.ruleId}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          rule.status === "success"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {rule.status === "success" ? "Success" : "Failed"}
                      </span>
                    </div>

                    {/* Merge Columns details */}
                    {rule.details?.mergeTo && (
                      <div className="mt-4 border-t pt-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <span className="text-sm text-gray-700">
                            Merged{" "}
                            <span className="font-mono font-semibold">
                              {rule.details.inputColumns?.join(" + ")}
                            </span>{" "}
                            →{" "}
                            <span className="font-mono font-semibold">
                              {rule.details.mergeTo}
                            </span>{" "}
                            ({rule.details.mergeDestination})
                          </span>
                          <span className="text-sm text-gray-500">
                            {rule.details.totalRows} rows processed
                          </span>
                        </div>

                        {/* Empty cell counts */}
                        {rule.details.emptyCounts && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {Object.entries(rule.details.emptyCounts).map(
                              ([col, count]) => (
                                <span
                                  key={col}
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    count > 0
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  Column {col}: {count} empty cell
                                  {count === 1 ? "" : "s"}
                                </span>
                              )
                            )}
                          </div>
                        )}

                        {/* Non-numeric value errors */}
                        {rule.details.invalidValues?.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
                            <div className="text-red-800 text-sm font-medium mb-1">
                              {rule.details.invalidValues.length} non-numeric
                              value{rule.details.invalidValues.length === 1 ? "" : "s"}{" "}
                              skipped
                            </div>
                            <ul className="text-xs text-red-700 max-h-24 overflow-y-auto list-disc pl-4">
                              {rule.details.invalidValues.map((err, idx) => (
                                <li key={idx}>
                                  Column {err.column}, row {err.row}: "{err.value}"
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Per-row merged values */}
                        {rule.details.rows?.length > 0 && (
                          <div className="bg-white rounded border">
                            <div className="px-3 py-2 bg-gray-100 border-b font-medium text-sm">
                              Merged Rows
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-1 text-left">Row</th>
                                    {rule.details.inputColumns.map((col) => (
                                      <th key={col} className="px-3 py-1 text-left">
                                        {col}
                                      </th>
                                    ))}
                                    <th className="px-3 py-1 text-left">Result</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {rule.details.rows.map((row) => (
                                    <tr key={row.row}>
                                      <td className="px-3 py-1 font-mono text-gray-500">
                                        {row.row}
                                      </td>
                                      {rule.details.inputColumns.map((col) => (
                                        <td key={col} className="px-3 py-1">
                                          {row.values[col] || (
                                            <span className="text-gray-400">
                                              empty
                                            </span>
                                          )}
                                        </td>
                                      ))}
                                      <td className="px-3 py-1 font-mono font-medium">
                                        {row.result === null ? (
                                          <span className="text-gray-400">—</span>
                                        ) : (
                                          row.result
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Rules Summary */}
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-800">
                    {
                      data.rulesExecution.executedRules.filter(
                        (r) => r.status === "success"
                      ).length
                    }
                  </div>
                  <div className="text-sm text-green-600">Successful</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-800">
                    {
                      data.rulesExecution.executedRules.filter(
                        (r) => r.status !== "success"
                      ).length
                    }
                  </div>
                  <div className="text-sm text-red-600">Failed</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-800">
                    {data.rulesExecution.totalRules}
                  </div>
                  <div className="text-sm text-blue-600">Total Rules</div>
                </div>
              </div>
            </AccordionSection>
          )}

          {/* Output Columns */}
          {(data.OutputColumns?.customeDataColumns?.length > 0 ||
            data.OutputColumns?.cloneColumns?.length > 0) && (
            <AccordionSection
              id="output-columns"
              title="Output Columns Configuration"
              icon={Database}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Custom Data Columns */}
                {data.OutputColumns.customeDataColumns?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">
                      Custom Data Columns
                    </h4>
                    <div className="space-y-2">
                      {data.OutputColumns.customeDataColumns.map(
                        (col, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center bg-gray-50 rounded p-3"
                          >
                            <span className="font-mono font-semibold">
                              Column {col.column}
                            </span>
                            <span className="text-gray-600 bg-white px-2 py-1 rounded border font-mono text-sm">
                              "{col.data}"
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Clone Columns */}
                {data.OutputColumns.cloneColumns?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">
                      Cloned Columns
                    </h4>
                    <div className="space-y-2">
                      {data.OutputColumns.cloneColumns.map((col, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center bg-gray-50 rounded p-3"
                        >
                          <span className="font-mono font-semibold">
                            Column {col.column}
                          </span>
                          <span className="text-gray-600 font-mono">
                            ← Column {col.sourceColumn}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AccordionSection>
          )}

          {/* Additional Operations */}
          {(data.sorting?.operations?.length > 0 ||
            data.rowRemoval?.operations?.length > 0) && (
            <AccordionSection
              id="additional-operations"
              title="Additional Operations"
              icon={RotateCcw}
            >
              {/* Sorting Operations */}
              {data.sorting?.operations?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Sorting Operations
                  </h4>
                  <div className="space-y-2">
                    {data.sorting.operations.map((op, index) => (
                      <div key={index} className="bg-gray-50 rounded p-3">
                        <div className="flex justify-between">
                          <span className="font-medium">
                            Sort Operation #{index + 1}
                          </span>
                          <span className="text-sm text-gray-600">
                            {op.rowsAffected || 0} rows affected
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Row Removal Operations */}
              {data.rowRemoval?.operations?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Row Removal Operations
                  </h4>
                  <div className="space-y-2">
                    {data.rowRemoval.operations.map((op, index) => (
                      <div
                        key={index}
                        className="bg-red-50 border border-red-200 rounded p-3"
                      >
                        <div className="flex justify-between">
                          <span className="font-medium text-red-800">
                            Removal Operation #{index + 1}
                          </span>
                          <span className="text-sm text-red-600">
                            {op.rowsRemoved || 0} rows removed
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-800">
                      {data.rowRemoval.totalRowsRemoved}
                    </div>
                    <div className="text-sm text-red-600">
                      Total Rows Removed
                    </div>
                  </div>
                </div>
              )}
            </AccordionSection>
          )}
        </div>

        {/* Footer */}
        {/* <div className="bg-gray-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Close
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default ExecutionSummaryModal;
